import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import type { AdminRole } from "../../utils/models";
import axiosInstance from "../../utils/networkWrapper";

export interface ActivityLogEntry {
	timestamp: string;
	pageSource: string;
	tableName: string;
	action: string;
	changes: Record<string, string | null>;
	details: Record<string, string | null>;
	changerUsername: string | undefined;
	changerAdminRoles: AdminRole[];
	changeeNurseId: number | undefined;
}

const ACTIVITY_LOG_QUERY_KEY = "activity-log";

export const useActivityLogQuery = ({
	start,
	end,
	changerUsername,
	changeeNurseId,
}: {
	start: DateTime;
	end: DateTime;
	changerUsername?: string;
	changeeNurseId?: number;
}): UseQueryResult<ActivityLogEntry[], Error> => {
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();

	return useQuery({
		queryKey: [ACTIVITY_LOG_QUERY_KEY, chosenDepartment?.department_id, start.toString(), end.toString(), changerUsername, changeeNurseId],
		queryFn: async () => {
			trackEvent("activity_log_data_fetch_started", { department_id: chosenDepartment?.department_id });

			const { data } = await axiosInstance.get<ActivityLogEntry[]>(`/activity-log/departments/${chosenDepartment.department_id}`, {
				params: {
					start: start.toFormat("yyyy-MM-dd"),
					end: end.toFormat("yyyy-MM-dd"),
					changer_username: changerUsername,
					changee_nurse_id: changeeNurseId,
				},
			});

			trackEvent("activity_log_data_fetch_success", { department_id: chosenDepartment?.department_id, entry_count: data.length });
			return data;
		},
		staleTime: 60 * 1000, // 1 minute
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id,
		meta: {
			onError: (error: unknown) => {
				showErrorSnackbar("Failed to load activity log", { action: "failed to fetch activity log" }, error);
				trackErrorEvent("activity_log_data_fetch_failed", error, {
					department_id: chosenDepartment?.department_id,
					error_source: "API",
				});
			},
		},
	});
};
